import { isAnyNativeEmbed, isEmbed } from '@databases/modes';
import { storage as createWithTemplateStorage } from '@databases/templates/storage';
import { useEffect, useState } from 'react';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import {
  PaywallFeature,
  useHasPaywallFeatureStateResolved,
  useIsPaywallUnlocked
} from '@canvas/paywall';

export const useShouldShowDatabaseBlankObjectExperience = ({
  contentId,
  isEmptyDatabase,
  isDbAiFirstCreationEnabled,
  isDatabaseLoaded,
  isInAIFCStagingAreaView
}: {
  contentId: string;
  isEmptyDatabase: boolean;
  isDbAiFirstCreationEnabled: boolean;
  isDatabaseLoaded: boolean;
  isInAIFCStagingAreaView: boolean;
}) => {
  const hasPaywallFeatureStateResolved = useHasPaywallFeatureStateResolved();
  const isAiTeammatePaywallUnlocked = useIsPaywallUnlocked(PaywallFeature.AI_TEAMMATE);

  const [shouldShowBlankObjectExperience, setShouldShowBlankObjectExperience] =
    useState<boolean>(false);

  useEffect(() => {
    const wasCreatedFromTemplate =
      createWithTemplateStorage.exists(contentId) &&
      isFeatureFlagEnabled('applyDatabaseTemplateFromQuickInsert');
    const isEmbeddedDatabase = isEmbed() || isAnyNativeEmbed();

    if (
      !isDatabaseLoaded ||
      !isEmptyDatabase ||
      !isDbAiFirstCreationEnabled ||
      !hasPaywallFeatureStateResolved ||
      !isAiTeammatePaywallUnlocked ||
      isInAIFCStagingAreaView ||
      isEmbeddedDatabase ||
      wasCreatedFromTemplate
    ) {
      setShouldShowBlankObjectExperience(false);
      return;
    }

    const isCwrBlankObjectExperimentEnabled = getExperimentValue(
      'cwrCanvasBlankObjectExperiment',
      'isEnabled',
      {
        fireExperimentExposure: true
      }
    );
    const cohort = isCwrBlankObjectExperimentEnabled
      ? getExperimentValue('cwrCanvasBlankObjectExperiment', 'cohort', {
          fireExperimentExposure: false
        })
      : 'control';
    const shouldShowExperience = cohort === 'variant1' || cohort === 'variant2';

    setShouldShowBlankObjectExperience(shouldShowExperience);
  }, [
    contentId,
    hasPaywallFeatureStateResolved,
    isAiTeammatePaywallUnlocked,
    isEmptyDatabase,
    isDatabaseLoaded,
    isDbAiFirstCreationEnabled,
    isInAIFCStagingAreaView
  ]);

  return shouldShowBlankObjectExperience;
};
